import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trophy, LogIn, X, Users, Camera } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppShell } from '../components/layout/AppShell';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { ErrorState } from '../components/common/ErrorState';
import { RankingGroupImage } from '../components/rankings/RankingGroupImage';
import {
  ScoringRulesFields,
  DEFAULT_SCORING_RULES,
} from '../components/rankings/ScoringRulesFields';
import { NamePrompt } from '../components/common/NamePrompt';
import { useCachedData } from '../hooks/useCachedData';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { api } from '../services/api';
import { RANKING_LIMITS } from '../config/rankingLimits';
import type { RankingSummary, ScoringRules } from '../types';

function SkeletonRankingCard() {
  return (
    <div className="glass-card rounded-2xl p-4 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/10 shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-4 w-32 bg-white/10 rounded" />
          <div className="h-3 w-24 bg-white/10 rounded" />
        </div>
        <div className="w-5 h-5 bg-white/10 rounded shrink-0" />
      </div>
    </div>
  );
}

interface RankingsListData {
  rankings: RankingSummary[];
}

export function RankingsPage() {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const fetcher = useCallback(() => api.rankings.list(), []);
  const { data, isLoading, error, didRefresh, refetch } = useCachedData<RankingsListData>({
    cacheKey: 'bt_rankings_list',
    fetcher,
  });
  const rankings = data?.rankings ?? [];
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  // The hub deep-links here with ?create=1 to open the modal straight away.
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setShowCreateModal(true);
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [joinError, setJoinError] = useState<string | null>(null);

  // Go through the invite page so paid rankings get the price + CPF flow.
  const handleJoinRanking = () => {
    if (!joinCode.trim()) return;
    setJoinError(null);
    navigate(`/rankings/join/${joinCode.trim().toUpperCase()}`);
  };

  const handleRankingCreated = () => {
    setShowCreateModal(false);
    refetch();
  };


  return (
    <AppShell>
      <div className="space-y-6 max-w-lg mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-2xl font-bold text-white">Rankings</h1>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            size="sm"
            disabled={isLoading}
          >
            <Plus className="w-4 h-4" />
            Criar
          </Button>
        </motion.div>

        {/* Join Ranking */}
        <Card delay={0.05} className={isLoading ? 'opacity-60 pointer-events-none' : ''}>
          <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
            <LogIn className="w-4 h-4 text-turquoise" />
            Entrar em um ranking
          </h3>
          <div className="flex flex-row gap-2">
            <input
              type="text"
              value={joinCode}
              onChange={(e) => {
                setJoinCode(e.target.value.toUpperCase());
                setJoinError(null);
              }}
              placeholder="Código de convite"
              maxLength={RANKING_LIMITS.INVITE_CODE_LENGTH}
              className="text-sm flex-1 px-4 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-turquoise/50 focus:ring-1 focus:ring-turquoise/30 font-mono text-center tracking-widest uppercase"
            />
            <Button
              onClick={handleJoinRanking}
              size="sm"
              disabled={!joinCode.trim()}
              noLoadingText
              style={{ minWidth: '74px' }}
            >
              Entrar
            </Button>
          </div>
          {joinError && (
            <p className="text-coral text-sm mt-2">{joinError}</p>
          )}
        </Card>

        {/* Loading */}
        {isLoading && (
          <div className="space-y-3">
            <SkeletonRankingCard />
            <SkeletonRankingCard />
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && rankings.length === 0 && (
          <ErrorState
            message={error}
            isOffline={!isOnline}
            onRetry={refetch}
          />
        )}

        {/* Empty State */}
        {!isLoading && !error && rankings.length === 0 && (
          <Card delay={0.1}>
            <div className="text-center py-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
                <Trophy className="w-8 h-8 text-white/30" />
              </div>
              <h3 className="font-semibold text-white mb-2">Nenhum ranking ainda</h3>
              <p className="text-sm text-white/50 mb-4">
                Crie um ranking e convide seus amigos para competir!
              </p>
            </div>
          </Card>
        )}

        {/* Rankings List */}
        {!isLoading && rankings.length > 0 && (
          <div className={`space-y-3 rounded-xl ${didRefresh ? 'animate-data-refresh' : ''}`}>
            {rankings.map((ranking, index) => (
              <motion.div
                key={ranking.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
              >
                <button
                  onClick={() => navigate(`/rankings/${ranking.id}`)}
                  className="w-full glass-card rounded-2xl p-4 text-left hover:bg-white/[0.08] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <RankingGroupImage imageUrl={ranking.imageUrl} size="md" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-white truncate">
                          {ranking.name}
                        </p>
                        {ranking.myRole === 'admin' && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-turquoise/20 text-turquoise">
                            Admin
                          </span>
                        )}
                        {ranking.status === 'finished' && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/10 text-white/50">
                            Encerrado
                          </span>
                        )}
                        {ranking.myStatus === 'pending_payment' && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-sunny/10 text-sunny">
                            Pagamento pendente
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-white/40 mt-0.5 mb-1">
                        {ranking.subtitle}
                        {ranking.seasonCount > 1 && (
                          <span className="ml-1 text-white/30">· {ranking.seasonCount} temporadas</span>
                        )}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <p className="text-sm text-white/50 flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {ranking.playerCount}
                        </p>
                        {ranking.currentRoundNumber && (
                          <p className="text-sm text-white/50">
                            Rodada {ranking.currentRoundNumber}
                            {ranking.totalRounds ? ` de ${ranking.totalRounds}` : ''}
                          </p>
                        )}
                        {ranking.pendingPlayerCount > 0 && ranking.myRole === 'admin' && (
                          <span className="text-xs text-sunny">
                            {ranking.pendingPlayerCount} pendente{ranking.pendingPlayerCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-white/30">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        )}

      </div>

      {/* Create Ranking Modal */}
      <CreateRankingModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleRankingCreated}
      />
    </AppShell>
  );
}

// ============================================================================
// Create Ranking Modal
// ============================================================================

function CreateRankingModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [showScoring, setShowScoring] = useState(false);
  const [scoring, setScoring] = useState<ScoringRules>(DEFAULT_SCORING_RULES);
  const [showFormat, setShowFormat] = useState(false);
  const [totalRounds, setTotalRounds] = useState('');
  const [discardRounds, setDiscardRounds] = useState('');
  // Paid participation (Asaas) — can only be enabled here, never after creation.
  const [chargeEntry, setChargeEntry] = useState(false);
  const [asaasConnected, setAsaasConnected] = useState<boolean | null>(null);
  const [asaasKey, setAsaasKey] = useState('');
  const [isConnectingAsaas, setIsConnectingAsaas] = useState(false);
  const [asaasError, setAsaasError] = useState<string | null>(null);
  const [feeAmount, setFeeAmount] = useState('');
  const [feeDueDays, setFeeDueDays] = useState('3');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { subscriber } = useAuth();

  // A ranking admin shows up in their own standings — get a display name first.
  const needsName = !subscriber?.name?.trim();

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setImageBase64(base64);
      setImagePreview(base64);
    };
    reader.readAsDataURL(file);
  };

  // Season format: an empty "total de rodadas" means the season declares no
  // length, and discards are then meaningless.
  const parsedTotalRounds = showFormat && totalRounds.trim() ? parseInt(totalRounds, 10) : null;
  const parsedDiscardRounds =
    parsedTotalRounds !== null && discardRounds.trim() ? parseInt(discardRounds, 10) : 0;
  const formatError =
    parsedTotalRounds !== null &&
      (parsedTotalRounds < 1 || parsedTotalRounds > RANKING_LIMITS.MAX_ROUNDS_PER_SEASON)
      ? `O total de rodadas deve estar entre 1 e ${RANKING_LIMITS.MAX_ROUNDS_PER_SEASON}`
      : parsedTotalRounds !== null && parsedDiscardRounds >= parsedTotalRounds
        ? 'Os descartes devem ser menos que o total de rodadas'
        : null;

  const handleToggleCharge = async () => {
    const next = !chargeEntry;
    setChargeEntry(next);
    setAsaasError(null);
    if (next && asaasConnected === null) {
      try {
        const res = await api.asaas.status();
        setAsaasConnected(res.success ? !!res.data?.status.connected : false);
      } catch {
        setAsaasConnected(false);
      }
    }
  };

  const handleConnectAsaas = async () => {
    if (!asaasKey.trim()) return;
    setIsConnectingAsaas(true);
    setAsaasError(null);
    try {
      const res = await api.asaas.connect(asaasKey.trim());
      if (res.success) {
        setAsaasConnected(true);
        setAsaasKey('');
      } else {
        setAsaasError(res.error?.message || 'Chave de API inválida');
      }
    } catch {
      setAsaasError('Erro ao conectar com o Asaas');
    } finally {
      setIsConnectingAsaas(false);
    }
  };

  const feeAmountCents = feeAmount.trim()
    ? Math.round(parseFloat(feeAmount.replace(',', '.')) * 100)
    : NaN;
  const feeDays = parseInt(feeDueDays, 10);
  const paymentError = !chargeEntry
    ? null
    : asaasConnected === false
      ? 'Conecte sua conta Asaas para cobrar inscrições'
      : !Number.isFinite(feeAmountCents) || feeAmountCents < 500
        ? 'Valor mínimo da inscrição: R$ 5,00'
        : !Number.isInteger(feeDays) || feeDays < 1 || feeDays > 30
          ? 'Prazo de pagamento deve ser de 1 a 30 dias'
          : null;

  const handleCreate = async () => {
    if (!name.trim() || formatError || paymentError) return;
    setIsCreating(true);
    setError(null);

    try {
      const scoringRules = showScoring ? scoring : undefined;

      const response = await api.rankings.create({
        name: name.trim(),
        description: description.trim() || undefined,
        scoringRules,
        totalRounds: parsedTotalRounds,
        discardRounds: parsedDiscardRounds,
        ...(chargeEntry
          ? { payment: { enabled: true, amountCents: feeAmountCents, dueDays: feeDays } }
          : {}),
      });
      if (response.success && response.data) {
        // Upload image if one was selected
        if (imageBase64) {
          const imgRes = await api.rankings.uploadGroupImage(response.data.id, imageBase64);
          if (!imgRes.success) {
            alert(
              imgRes.error?.message ||
              'Ranking criado, mas não foi possível enviar a imagem. Tente novamente na página do ranking.'
            );
          }
        }
        onCreated();
        navigate(`/rankings/${response.data.id}`);
      } else {
        setError(response.error?.message || 'Erro ao criar ranking');
      }
    } catch {
      setError('Erro ao criar ranking');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm glass-card rounded-2xl p-6 max-h-[85vh] overflow-y-auto"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display text-xl font-bold text-white">
                Criar ranking
              </h3>
              <button
                onClick={onClose}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {needsName ? (
              <NamePrompt subtitle="Esse é o nome que os outros jogadores vão ver no ranking." />
            ) : (
              <>
                {/* Image */}
                <div className="flex flex-col items-center mb-5">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-20 h-20 rounded-xl relative group overflow-hidden shrink-0"
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-white/10 flex flex-col items-center justify-center">
                        <Camera className="w-6 h-6 text-white/40" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera className="w-5 h-5 text-white" />
                    </div>
                  </button>
                  <p className="text-xs text-white/40 mt-2">
                    {imagePreview ? 'Toque para trocar a foto' : 'Adicionar foto (opcional)'}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </div>

                {/* Name */}
                <div className="mb-4">
                  <label className="block text-sm text-white/60 mb-2">Nome do ranking</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex: Ranking da Arena, BT Cup..."
                    maxLength={RANKING_LIMITS.MAX_NAME_LENGTH}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-turquoise/50 focus:ring-1 focus:ring-turquoise/30"
                    autoFocus
                  />
                  <p className="text-xs text-white/30 mt-1 text-right">
                    {name.length}/{RANKING_LIMITS.MAX_NAME_LENGTH}
                  </p>
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm text-white/60 mb-2">Descrição (opcional)</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descreva o ranking..."
                    maxLength={RANKING_LIMITS.MAX_DESCRIPTION_LENGTH}
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-turquoise/50 focus:ring-1 focus:ring-turquoise/30 resize-none"
                  />
                </div>

                {/* Scoring toggle */}
                <button
                  onClick={() => setShowScoring(!showScoring)}
                  className="text-sm text-turquoise hover:text-turquoise/80 mb-4 transition-colors"
                >
                  {showScoring ? 'Usar pontuação padrão' : 'Personalizar pontuação'}
                </button>

                {showScoring && (
                  <div className="space-y-3 mb-4 p-3 rounded-xl bg-white/5">
                    <ScoringRulesFields
                      value={scoring}
                      onChange={(patch) => setScoring((prev) => ({ ...prev, ...patch }))}
                      adjustBaseOnModeChange
                    />
                  </div>
                )}

                {/* Season format toggle */}
                <button
                  onClick={() => setShowFormat(!showFormat)}
                  className="block text-sm text-turquoise hover:text-turquoise/80 mb-4 transition-colors"
                >
                  {showFormat ? 'Temporada sem número fixo de rodadas' : 'Definir rodadas e descartes'}
                </button>

                {showFormat && (
                  <div className="space-y-3 mb-4 p-3 rounded-xl bg-white/5">
                    <RoundsInput
                      label="Total de rodadas"
                      value={totalRounds}
                      onChange={setTotalRounds}
                      placeholder="—"
                    />
                    <RoundsInput
                      label="Rodadas de descarte"
                      value={discardRounds}
                      onChange={setDiscardRounds}
                      placeholder="0"
                      disabled={!totalRounds.trim()}
                    />
                    <p className="text-[11px] text-white/40 leading-snug">
                      {parsedTotalRounds !== null && parsedDiscardRounds > 0
                        ? `Cada jogador soma apenas suas ${parsedTotalRounds - parsedDiscardRounds} melhores rodadas. Rodadas não jogadas contam como descarte.`
                        : 'Deixe em branco para que todas as rodadas contem, sem descartes.'}
                    </p>
                  </div>
                )}

                {/* Paid participation toggle */}
                <button
                  onClick={handleToggleCharge}
                  className="block text-sm text-turquoise hover:text-turquoise/80 mb-4 transition-colors"
                >
                  {chargeEntry ? 'Entrada gratuita' : 'Cobrar inscrição (PIX)'}
                </button>

                {chargeEntry && (
                  <div className="space-y-3 mb-4 p-3 rounded-xl bg-white/5">
                    {asaasConnected === null && (
                      <p className="text-xs text-white/40">Verificando conta Asaas…</p>
                    )}
                    {asaasConnected === false && (
                      <>
                        <p className="text-xs text-white/60 leading-snug">
                          Conecte sua conta <strong>Asaas</strong> para receber os pagamentos.
                          Cole aqui sua chave de API (Asaas → Integrações → Chave de API).
                        </p>
                        <input
                          type="password"
                          value={asaasKey}
                          onChange={(e) => setAsaasKey(e.target.value)}
                          placeholder="Chave de API do Asaas"
                          className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white text-sm placeholder-white/25 focus:outline-none focus:border-turquoise/50"
                        />
                        <Button
                          onClick={handleConnectAsaas}
                          size="sm"
                          fullWidth
                          isLoading={isConnectingAsaas}
                          disabled={!asaasKey.trim()}
                        >
                          Conectar Asaas
                        </Button>
                        <p className="text-[11px] text-white/40 leading-snug">
                          Dica: cadastre uma chave PIX na sua conta Asaas para que os QR Codes
                          não expirem no mesmo dia.
                        </p>
                      </>
                    )}
                    {asaasConnected === true && (
                      <>
                        <p className="text-xs text-green-400">✓ Conta Asaas conectada</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/60">Valor (R$)</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={feeAmount}
                            placeholder="30,00"
                            onChange={(e) => setFeeAmount(e.target.value.replace(/[^\d.,]/g, ''))}
                            className="w-24 px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 text-white text-center text-sm placeholder-white/25 focus:outline-none focus:border-turquoise/50"
                          />
                        </div>
                        <RoundsInput
                          label="Prazo de pagamento (dias)"
                          value={feeDueDays}
                          onChange={setFeeDueDays}
                          placeholder="3"
                        />
                        <p className="text-[11px] text-white/40 leading-snug">
                          Quem entrar paga via PIX direto para sua conta Asaas e só participa
                          após o pagamento. Isso não poderá ser alterado depois — apenas o
                          valor e o prazo.
                        </p>
                      </>
                    )}
                    {asaasError && <p className="text-coral text-xs">{asaasError}</p>}
                  </div>
                )}

                {(error || formatError || paymentError) && (
                  <p className="text-coral text-sm mb-4">{error || formatError || paymentError}</p>
                )}

                <Button
                  onClick={handleCreate}
                  fullWidth
                  isLoading={isCreating}
                  disabled={!name.trim() || !!formatError || !!paymentError}
                >
                  <Plus className="w-4 h-4" />
                  Criar ranking
                </Button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function RoundsInput({
  label,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${disabled ? 'text-white/30' : 'text-white/60'}`}>{label}</span>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '' || /^\d{1,3}$/.test(v)) onChange(v);
        }}
        className="w-20 px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 text-white text-center text-sm placeholder-white/25 focus:outline-none focus:border-turquoise/50 disabled:opacity-40"
      />
    </div>
  );
}

